import type { Category, Comment, GeocodeResult, Photo, Place, TripState, Vote } from '@map/shared';

/**
 * Erreur d'API portant le statut HTTP. Les ecrans decident quoi montrer en
 * fonction du statut (401 -> retour a l'ecran de code, 404 -> la fiche a ete
 * supprimee par quelqu'un d'autre) : sans le statut, ils devraient deviner en
 * lisant le texte du message, ce qui casse a la premiere reformulation.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly retryAfterSeconds: number | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface SessionResult {
  readonly memberId: number;
  readonly pseudo: string;
  readonly reused: boolean;
}

export interface PlaceInput {
  readonly lat: number;
  readonly lng: number;
  readonly title: string;
  readonly description: string;
  readonly category: Category;
  readonly url: string | null;
}

export type PlacePatch = Partial<PlaceInput>;

interface RequestOptions {
  readonly method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly body?: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readRetryAfter(response: Response): number | null {
  const raw = response.headers.get('retry-after');
  if (raw === null) return null;
  const seconds = Number.parseInt(raw, 10);
  return Number.isFinite(seconds) ? seconds : null;
}

async function readErrorBody(response: Response): Promise<{ code: string; message: string }> {
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  const code = isRecord(payload) && typeof payload['error'] === 'string' ? payload['error'] : 'http_error';
  const message =
    isRecord(payload) && typeof payload['message'] === 'string'
      ? payload['message']
      : `Requete refusee (${response.status}).`;
  return { code, message };
}

/**
 * Toutes les requetes partent en `same-origin` : le cookie de session est
 * httpOnly, le client ne peut pas le lire ni le poser lui-meme. C'est aussi
 * pourquoi aucune URL absolue n'apparait ici — parler a une autre origine
 * ferait silencieusement perdre l'authentification.
 */
async function request<T>(path: string, options: RequestOptions): Promise<T> {
  const init: RequestInit = {
    method: options.method,
    credentials: 'same-origin',
  };
  if (options.body !== undefined) {
    init.headers = { 'content-type': 'application/json' };
    init.body = JSON.stringify(options.body);
  }

  let response: Response;
  try {
    response = await fetch(path, init);
  } catch {
    throw new ApiError(0, 'network', 'Le serveur est injoignable.');
  }

  if (!response.ok) {
    const { code, message } = await readErrorBody(response);
    throw new ApiError(response.status, code, message, readRetryAfter(response));
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

// --- session ---------------------------------------------------------------

export function openSession(code: string, pseudo: string): Promise<SessionResult> {
  return request<SessionResult>('/api/session', { method: 'POST', body: { code, pseudo } });
}

export function fetchState(): Promise<TripState> {
  return request<TripState>('/api/state', { method: 'GET' });
}

// --- recherche de lieu -----------------------------------------------------

/**
 * Cherche un toponyme. Le serveur relaie Nominatim : le navigateur ne peut pas
 * poser le `User-Agent` que sa politique d'usage exige, et la cadence d'une
 * requete par seconde ne se tient qu'a un seul endroit.
 *
 * Ne cree rien : le resultat sert a deplacer la carte, pas a poser un lieu.
 */
export function searchPlaces(q: string): Promise<GeocodeResult[]> {
  return request<GeocodeResult[]>(`/api/geocode?q=${encodeURIComponent(q)}`, { method: 'GET' });
}

// --- lieux -----------------------------------------------------------------

export function createPlace(input: PlaceInput): Promise<Place> {
  return request<Place>('/api/places', { method: 'POST', body: input });
}

export function patchPlace(placeId: number, patch: PlacePatch): Promise<Place> {
  return request<Place>(`/api/places/${placeId}`, { method: 'PATCH', body: patch });
}

export function deletePlace(placeId: number): Promise<void> {
  return request<void>(`/api/places/${placeId}`, { method: 'DELETE' });
}

// --- votes -----------------------------------------------------------------

export function putVote(placeId: number, value: -1 | 1): Promise<Vote> {
  return request<Vote>(`/api/places/${placeId}/vote`, { method: 'PUT', body: { value } });
}

export function deleteVote(placeId: number): Promise<void> {
  return request<void>(`/api/places/${placeId}/vote`, { method: 'DELETE' });
}

// --- commentaires ----------------------------------------------------------

export function addComment(placeId: number, body: string): Promise<Comment> {
  return request<Comment>(`/api/places/${placeId}/comments`, { method: 'POST', body: { body } });
}

export function deleteComment(commentId: number): Promise<void> {
  return request<void>(`/api/comments/${commentId}`, { method: 'DELETE' });
}

// --- photos ----------------------------------------------------------------

export function deletePhoto(photoId: number): Promise<void> {
  return request<void>(`/api/photos/${photoId}`, { method: 'DELETE' });
}

/**
 * L'upload passe par XMLHttpRequest et non par fetch : c'est le seul moyen
 * d'obtenir la progression d'envoi, dont la barre de progression de la
 * galerie a besoin. Une photo de telephone met plusieurs secondes a monter
 * sur une connexion mobile ; sans retour visuel l'utilisateur reclique.
 */
export function uploadPhoto(
  placeId: number,
  file: File,
  onProgress: (ratio: number) => void,
): Promise<Photo> {
  return new Promise<Photo>((resolve, reject) => {
    const form = new FormData();
    form.append('photo', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/places/${placeId}/photos`);
    xhr.withCredentials = true;

    xhr.upload.addEventListener('progress', (event) => {
      if (!event.lengthComputable || event.total === 0) return;
      onProgress(event.loaded / event.total);
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as Photo);
        } catch {
          reject(new ApiError(xhr.status, 'bad_payload', 'Reponse illisible du serveur.'));
        }
        return;
      }
      let code = 'http_error';
      let message = `Envoi refuse (${xhr.status}).`;
      try {
        const payload: unknown = JSON.parse(xhr.responseText);
        if (isRecord(payload)) {
          if (typeof payload['error'] === 'string') code = payload['error'];
          if (typeof payload['message'] === 'string') message = payload['message'];
        }
      } catch {
        // Corps non JSON (proxy, limite de taille) : le statut suffit.
      }
      reject(new ApiError(xhr.status, code, message));
    });

    xhr.addEventListener('error', () => {
      reject(new ApiError(0, 'network', 'Le serveur est injoignable.'));
    });

    xhr.send(form);
  });
}

// --- itineraire ------------------------------------------------------------

export function setItinerary(placeIds: readonly number[]): Promise<{ places: Place[] }> {
  return request<{ places: Place[] }>('/api/itinerary', {
    method: 'PUT',
    body: { placeIds: [...placeIds] },
  });
}

/** URL publique d'une photo pleine taille. */
export function photoUrl(filename: string): string {
  return `/uploads/${filename}`;
}

/** URL de la vignette 320 px produite par sharp a l'upload. */
export function thumbUrl(filename: string): string {
  const dot = filename.lastIndexOf('.');
  const stem = dot === -1 ? filename : filename.slice(0, dot);
  return `/uploads/${stem}-thumb.webp`;
}
